import React, { useState, useEffect } from 'react';
import { SubFeatureGate } from '../FeatureGate';
import api from '../../api';
import { BookOpen, Search, RefreshCw, Users, PlusCircle, Edit2, Trash2 } from 'lucide-react';
import '../StudentLMSSection.css'; // Reuse existing styles

const PartnerCourses = () => {
  const [courses, setCourses] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [batchOptions, setBatchOptions] = useState([{ label: "All Batches", value: "All Batches" }]);
  const [facultyList, setFacultyList] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [fullDescCourse, setFullDescCourse] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isViewAssignmentsModalOpen, setIsViewAssignmentsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState('');
  const [selectedPop, setSelectedPop] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [isEditingBatch, setIsEditingBatch] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ show: false, assignmentId: null });
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesRes, studentsRes, facultyRes] = await Promise.all([
        api.get('/integrations/edvergencex/uni-courses'),
        api.get('/students').catch(() => ({ data: [] })),
        api.get('/faculty').catch(() => ({ data: [] }))
      ]);

      setCourses(coursesRes.data?.data || []);
      setLicenseInfo(coursesRes.data?.license_info || null);
      setFacultyList(facultyRes.data || []);

      const studentList = studentsRes.data || [];
      const cohortMap = new Map();
      let totalStudents = studentList.length;

      studentList.forEach(s => {
        const year = s.academic_batch || s.graduation_year;
        const branch = s.branch || s.degree || s.department;
        const gradLevel = s.graduation_level;
        const program = s.program;

        const parts = [];
        if (gradLevel) parts.push(String(gradLevel).trim());
        if (program) parts.push(String(program).trim());
        if (branch) parts.push(String(branch).trim());

        const baseString = parts.join(' - ');

        let cohort = "";
        if (baseString && year) {
          cohort = `${baseString} (${String(year).trim()})`;
        } else if (baseString) {
          cohort = baseString;
        } else if (year) {
          cohort = `Batch of ${String(year).trim()}`;
        }

        if (cohort) {
          cohortMap.set(cohort, (cohortMap.get(cohort) || 0) + 1);
        }
      });

      const newBatchOptions = [
        { label: `All Batches (${totalStudents} students)`, value: "All Batches" }
      ];
      for (const [cohort, count] of cohortMap.entries()) {
        newBatchOptions.push({ label: `${cohort} (${count} students)`, value: cohort });
      }
      setBatchOptions(newBatchOptions);
    } catch (err) {
      console.error('Failed to fetch partner courses', err);
      setError("Failed to load partner courses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssignBatch = async () => {
    if (!selectedCourse || !selectedBatch) return;
    setAssigning(true);
    try {
      await api.post('/integrations/edvergencex/assign-course-to-batch', {
        course_id: selectedCourse.externalContentRootId,
        academic_batch: selectedBatch,
        faculty_id: selectedFaculty ? parseInt(selectedFaculty, 10) : null,
        pop_id: selectedPop ? parseInt(selectedPop, 10) : null
      });
      addToast(`Course assigned to ${selectedBatch} successfully!`);
      setIsAssignModalOpen(false);
      setSelectedCourse(null);
      setSelectedBatch('');
      setSelectedFaculty('');
      setSelectedPop('');
      fetchData();
    } catch (err) {
      console.error("Error assigning course to batch:", err);
      addToast(err.response?.data?.detail || "Failed to assign course to batch.", 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteBatch = (assignmentId) => {
    setConfirmDeleteModal({ show: true, assignmentId });
  };

  const executeDeleteBatch = async () => {
    if (!confirmDeleteModal.assignmentId) return;
    try {
      await api.delete(`/integrations/edvergencex/assign-course-to-batch/${confirmDeleteModal.assignmentId}`);
      addToast("Batch assignment removed successfully!");
      setConfirmDeleteModal({ show: false, assignmentId: null });
      setIsViewAssignmentsModalOpen(false);
      fetchData();
    } catch (err) {
      console.error("Error removing assignment:", err);
      addToast(err.response?.data?.detail || "Failed to remove assignment.", 'error');
      setConfirmDeleteModal({ show: false, assignmentId: null });
    }
  };

  const handleEditBatch = (batchObj) => {
    setIsViewAssignmentsModalOpen(false);
    setSelectedBatch(batchObj.academic_batch);
    setSelectedFaculty(batchObj.faculty_id ? String(batchObj.faculty_id) : '');
    setSelectedPop(batchObj.pop_id ? String(batchObj.pop_id) : '');
    setIsEditingBatch(true);
    setIsAssignModalOpen(true);
  };

  const filteredCourses = courses.filter(c =>
    (c.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <header className="page-header" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.8rem', color: 'var(--text-main)', fontWeight: 800 }}>Courses</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>Courses assigned to your university by the system admin.</p>
        </div>
        <button onClick={fetchData} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600, width: 'fit-content' }}>
          <RefreshCw size={18} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </header>

      {licenseInfo && licenseInfo.total_lms_licenses !== null && (
        <div style={{ marginBottom: '24px', padding: '16px 20px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: 'var(--text-main)' }}>License Usage</h3>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              You have used <strong>{licenseInfo.used_lms_licenses}</strong> out of <strong>{licenseInfo.total_lms_licenses}</strong> allocated licenses.
            </p>
          </div>
          <div style={{ background: licenseInfo.used_lms_licenses >= licenseInfo.total_lms_licenses ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)', color: licenseInfo.used_lms_licenses >= licenseInfo.total_lms_licenses ? '#ef4444' : '#10b981', padding: '8px 16px', borderRadius: '8px', fontWeight: 600 }}>
            {licenseInfo.total_lms_licenses - licenseInfo.used_lms_licenses} Remaining
          </div>
        </div>
      )}

      <div className="lms-search-container" style={{ marginBottom: '24px' }}>
        <div className="search-input-wrapper" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '0 16px', flex: 1 }}>
          <Search size={20} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search assigned courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ border: 'none', background: 'transparent', padding: '16px', outline: 'none', width: '100%', color: 'var(--text-main)' }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading courses...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>{error}</div>
      ) : filteredCourses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' }}>
          <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: 'var(--text-main)' }}>No Courses Available</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>You don't have any partner courses assigned to your university yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
          {filteredCourses.map(course => (
            <div key={course.externalContentRootId} style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: '160px', overflow: 'hidden', borderBottom: '1px solid var(--border)' }}>
                <img src={course.thumbnailUrl || 'https://via.placeholder.com/300x160'} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: 'var(--text-main)' }}>{course.title}</h3>
                <div style={{ flex: 1, marginBottom: '16px' }}>
                  <p className="course-card-description" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>{course.description}</p>
                  {course.description && course.description.length > 150 && (
                    <button
                      onClick={() => setFullDescCourse(course)}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.85rem', fontWeight: 600, marginTop: '4px' }}
                    >
                      Read More
                    </button>
                  )}
                </div>
                <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  <strong style={{ color: 'var(--text-main)', display: 'block', marginBottom: '6px' }}>Assigned to:</strong>
                  {course.assigned_batches && course.assigned_batches.length > 0 ? (
                    <button
                      onClick={() => {
                        setSelectedCourse(course);
                        setIsViewAssignmentsModalOpen(true);
                      }}
                      style={{
                        background: 'none', border: 'none', color: 'var(--primary)',
                        cursor: 'pointer', padding: 0, textDecoration: 'underline',
                        fontSize: '0.8rem', fontWeight: 600, textAlign: 'left'
                      }}
                    >
                      View {course.assigned_batches.length} Batch Assignment{course.assigned_batches.length === 1 ? '' : 's'}
                    </button>
                  ) : (
                    <span>None</span>
                  )}
                </div>
                <SubFeatureGate feature="lms" sub="lms_course_assign">
                <button
                  className="stu-lms-btn-start"
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                  onClick={() => {
                    setSelectedCourse(course);
                    setIsAssignModalOpen(true);
                    setSelectedBatch('');
                    setSelectedFaculty('');
                    setSelectedPop('');
                    setIsEditingBatch(false);
                  }}
                >
                  <Users size={18} /> Assign to Batch
                </button>
                </SubFeatureGate>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAssignModalOpen && selectedCourse && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '500px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Assign Course</h2>
              <button onClick={() => setIsAssignModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ marginBottom: '16px' }}>Assign <strong>{selectedCourse.title}</strong> to a student batch.</p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Select Batch</label>
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  disabled={isEditingBatch}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', outline: 'none', opacity: isEditingBatch ? 0.6 : 1 }}
                >
                  <option value="">-- Select Academic Batch --</option>
                  {batchOptions.map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>

              {selectedBatch && (
                <>
                  {/* Faculty Dropdown */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Select Faculty Member <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>(Optional)</span></label>
                    <select
                      value={selectedFaculty}
                      onChange={(e) => setSelectedFaculty(e.target.value)}
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', outline: 'none' }}
                    >
                      <option value="">-- Select Faculty Member --</option>
                      {facultyList.map(f => (
                        <option key={`fac-${f.id}`} value={f.id}>
                          {f.full_name} {f.department ? `(${f.department})` : ''} {f.designation ? `- ${f.designation}` : ''}
                        </option>
                      ))}
                    </select>
                    {facultyList.length === 0 && (
                      <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No faculty members found.</span>
                    )}
                  </div>

                  {/* PoP Dropdown */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Select Professor of Practice <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>(Optional)</span></label>
                    <select
                      value={selectedPop}
                      onChange={(e) => setSelectedPop(e.target.value)}
                      style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', outline: 'none' }}
                    >
                      <option value="">-- Select Professor of Practice --</option>
                      {selectedCourse.assigned_pops && selectedCourse.assigned_pops.map(p => (
                        <option key={`pop-${p.id}`} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {(!selectedCourse.assigned_pops || selectedCourse.assigned_pops.length === 0) && (
                      <span style={{ display: 'block', marginTop: '6px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>No PoPs assigned to this course for your university yet.</span>
                    )}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => setIsAssignModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button
                  onClick={handleAssignBatch}
                  disabled={assigning || !selectedBatch}
                  style={{ padding: '10px 20px', borderRadius: '8px', background: 'var(--primary)', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (!selectedBatch || assigning) ? 0.6 : 1 }}
                >
                  {assigning ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Assignments Modal */}
      {isViewAssignmentsModalOpen && selectedCourse && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999 }}>
          <div style={{ background: 'var(--bg-card)', padding: '30px', borderRadius: '24px', width: '100%', maxWidth: '500px', border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.5rem', color: 'var(--text-main)' }}>Assigned Batches</h2>
            <p style={{ margin: '0 0 20px', color: 'var(--text-muted)' }}>{selectedCourse.title}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', paddingRight: '8px', flex: 1, marginBottom: '20px' }}>
              {selectedCourse.assigned_batch_details?.map((batchObj, idx) => (
                <div key={idx} style={{ padding: '16px', background: 'var(--bg-card-hover)', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Users size={20} color="var(--primary)" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ color: 'var(--text-main)', fontWeight: 500 }}>
                        {batchObj.academic_batch}
                      </span>
                      {batchObj.faculty_name && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          Faculty: {batchObj.faculty_name}
                        </span>
                      )}
                      {batchObj.pop_name && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          PoP: {batchObj.pop_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEditBatch(batchObj)}
                      style={{ background: 'var(--surface-hover)', border: '1px solid var(--border)', color: 'var(--text-main)', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}
                      title="Edit Assignment"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteBatch(batchObj.id)}
                      style={{ background: 'rgba(239, 68, 68, 0.1)', border: 'none', color: '#ef4444', padding: '6px', borderRadius: '6px', cursor: 'pointer' }}
                      title="Delete Assignment"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setIsViewAssignmentsModalOpen(false)}
              style={{ padding: '12px 24px', borderRadius: '12px', background: 'var(--surface-hover)', color: 'var(--text-main)', border: '1px solid var(--border)', fontWeight: 600, cursor: 'pointer', width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}


      {/* Toasts */}
      <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 999999, display: 'flex', flexDirection: 'column', gap: '12px', pointerEvents: 'none' }}>
        {toasts.map(toast => (
          <div key={toast.id} style={{ background: toast.type === 'error' ? '#ef4444' : '#10b981', color: '#fff', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', pointerEvents: 'auto' }}>
            {toast.message}
          </div>
        ))}
      </div>
      {/* Full Description Modal */}
      {fullDescCourse && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{fullDescCourse.title}</h3>
              <button className="modal-close" onClick={() => setFullDescCourse(null)}>×</button>
            </div>
            <div className="modal-content custom-scrollbar" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {fullDescCourse.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDeleteModal.show && (
        <div className="modal-overlay" style={{ zIndex: 9999999 }}>
          <div className="modal-box" style={{ maxWidth: '400px' }}>
            <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <h3 style={{ color: 'var(--text-main)', fontSize: '1.25rem', margin: 0 }}>Remove Assignment</h3>
            </div>
            <div className="modal-content" style={{ paddingTop: '16px', paddingBottom: '24px' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>Are you sure you want to remove this batch assignment? This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => setConfirmDeleteModal({ show: false, assignmentId: null })}
                  style={{ padding: '8px 16px', borderRadius: '8px', background: 'var(--surface-hover)', border: '1px solid var(--border)', color: 'var(--text-main)', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={executeDeleteBatch}
                  style={{ padding: '8px 16px', borderRadius: '8px', background: '#ef4444', border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PartnerCourses;
